import { useState, useEffect } from 'react'
import { Search, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { aiConfigService } from '../lib/aiService'

interface SearchBarProps {
  compact?: boolean
  onSearch?: (query: string) => void
}

const SearchBar = ({ compact = false, onSearch }: SearchBarProps) => {
  const [query, setQuery] = useState('')
  const [isAISearchEnabled, setIsAISearchEnabled] = useState(false)
  const [isAIAvailable, setIsAIAvailable] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    // Verificar se API de IA está configurada
    const checkAIAvailability = async () => {
      try {
        const available = await aiConfigService.isAIAvailable()
        setIsAIAvailable(available)
      } catch (error) {
        console.error('Erro ao verificar disponibilidade de IA:', error)
        setIsAIAvailable(false)
      }
    }
    checkAIAvailability()
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return
    
    if (isAISearchEnabled && isAIAvailable) {
      // Navegar para página de busca com IA
      navigate(`/ai-search?q=${encodeURIComponent(query)}`)
    } else {
      // Busca normal - redirecionar para página de busca unificada
      navigate(`/search?q=${encodeURIComponent(query)}`)
    }
    
    if (onSearch) {
      onSearch(query)
    }
  }

  const toggleAISearch = () => {
    if (isAIAvailable) {
      setIsAISearchEnabled(!isAISearchEnabled)
    }
  }

  return (
    <div className={`flex items-center ${compact ? 'gap-2' : 'gap-3'}`}>
      <form onSubmit={handleSubmit} className="flex-1 relative">
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={isAISearchEnabled ? "Pergunte algo sobre o portal..." : "Buscar notícias, anúncios, grupos..."}
            className={`w-full ${compact ? 'pl-10 pr-10 py-2 text-sm' : 'pl-12 pr-12 py-3'} bg-noe-900/50 border ${isAISearchEnabled ? 'border-purple-500/50' : 'border-noe-800'} rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 ${isAISearchEnabled ? 'focus:ring-purple-500/50' : 'focus:ring-noe-brand/50'} focus:border-noe-brand transition-all`}
          />
          <Search className={`absolute ${compact ? 'left-3 w-4 h-4' : 'left-4 w-5 h-5'} top-1/2 transform -translate-y-1/2 text-noe-500`} />
          
          {isAIAvailable && (
            <button
              type="button"
              onClick={toggleAISearch}
              className={`absolute ${compact ? 'right-3 w-4 h-4' : 'right-4 w-5 h-5'} top-1/2 transform -translate-y-1/2 ${isAISearchEnabled ? 'text-purple-400' : 'text-noe-500 hover:text-noe-300'} transition-colors`}
              title={isAISearchEnabled ? "Busca inteligente ativada" : "Ativar busca inteligente (IA)"}
            >
              <Sparkles className={`w-full h-full ${isAISearchEnabled ? 'fill-purple-400/20' : ''}`} />
            </button>
          )}
        </div>
        
        {isAISearchEnabled && (
          <div className="mt-2 text-xs text-purple-400 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            <span>Busca inteligente ativada - faça perguntas em linguagem natural</span>
          </div>
        )}
      </form>
      
      {!compact && (
        <button
          type="submit"
          onClick={handleSubmit}
          className="px-4 py-3 bg-noe-brand text-white font-medium rounded-xl hover:bg-noe-brandHover transition-colors"
        >
          Buscar
        </button>
      )}
    </div>
  )
}

export default SearchBar